// =============================================================================
// GiftCardOrderPage — download the gift cards of an online order (gift-card-shop)
// =============================================================================
// manage.neoseum.com/cadeaubon?t=<token>, the link in the buyer and recipient
// mails of the gift card shop (/gift-card-v2). Lists every card of the order
// with its code and last valid day, one PDF per card and one with all of them.
// The 48-character token is the only key (no login); booking-giftcard-order-file
// answers only for a paid order and never returns e-mail addresses.
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_BOOKING_API_URL as string;
const ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

interface Card { serial: number; code: string; code_display: string; expires_on: string; display_status: string }
interface Order {
  locale: 'nl' | 'en';
  value_kind: 'tickets' | 'amount';
  tickets: number | null;
  card_value_cents: number;
  design: string;
  to: string | null;
  from: string | null;
  message: string | null;
  cards: Card[];
  pdf_base: string;
}

const DESIGNS: Record<string, string> = { maya: 'Mystery of the Maya', pharaohsvr: 'Voyage of the Pharaohs', odyssey: 'A Star Odyssey' };

const T = {
  nl: {
    head: 'Je cadeaubonnen',
    one: 'Je cadeaubon',
    loading: 'Laden…',
    notFound: 'Deze link werkt niet (meer). Gebruik de link uit je e-mail, of mail bookings@neoseum.com.',
    error: 'Er ging iets mis. Probeer het opnieuw of mail bookings@neoseum.com.',
    worth: (v: string) => `Goed voor ${v}`,
    until: (d: string) => `Geldig t/m ${d}`,
    to: 'Voor', from: 'Van',
    pdf: 'Download pdf',
    all: 'Alles in één pdf',
    book: 'Boek met deze bon',
    how: 'Inwisselen: vul de code in op neoseum.com/book, of scan de QR-code op de bon. Geldig voor elke ervaring in Eindhoven en Amsterdam.',
    used: 'Gebruikt', blocked: 'Geblokkeerd', expired: 'Verlopen',
    questions: 'Vragen?',
  },
  en: {
    head: 'Your gift cards',
    one: 'Your gift card',
    loading: 'Loading…',
    notFound: 'This link does not work (any more). Use the link from your e-mail, or write to bookings@neoseum.com.',
    error: 'Something went wrong. Try again or write to bookings@neoseum.com.',
    worth: (v: string) => `Worth ${v}`,
    until: (d: string) => `Valid until ${d}`,
    to: 'To', from: 'From',
    pdf: 'Download PDF',
    all: 'All in one PDF',
    book: 'Book with this card',
    how: 'To redeem: enter the code at neoseum.com/book, or scan the QR code on the card. Valid for every experience in Eindhoven and Amsterdam.',
    used: 'Used', blocked: 'Blocked', expired: 'Expired',
    questions: 'Questions?',
  },
};

const money = (cents: number, lang: 'nl' | 'en') =>
  new Intl.NumberFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', { style: 'currency', currency: 'EUR' }).format(cents / 100);
const day = (d: string, lang: 'nl' | 'en') => {
  const [y, m, dd] = d.split('-').map(Number);
  return new Intl.DateTimeFormat(lang === 'nl' ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y!, m! - 1, dd!)));
};

export function isGiftCardOrderRoute(): boolean {
  return window.location.pathname.replace(/\/+$/, '') === '/cadeaubon';
}

export function GiftCardOrderPage() {
  const token = new URLSearchParams(window.location.search).get('t') ?? '';
  const [state, setState] = useState<'loading' | 'ok' | 'not_found' | 'error'>('loading');
  const [order, setOrder] = useState<Order | null>(null);
  const lang: 'nl' | 'en' = order?.locale ?? (navigator.language?.startsWith('nl') ? 'nl' : 'en');
  const c = T[lang];

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/booking-giftcard-order-file`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: ANON, Authorization: `Bearer ${ANON}` },
          body: JSON.stringify({ token }),
        });
        if (!live) return;
        if (res.status === 404) { setState('not_found'); return; }
        if (!res.ok) { setState('error'); return; }
        // The PDF links are built here from our own API address (always https).
        const o = (await res.json()) as Order;
        setOrder({ ...o, pdf_base: `${API}/booking-giftcard-order-file?t=${encodeURIComponent(token)}` });
        setState('ok');
      } catch {
        if (live) setState('error');
      }
    })();
    return () => { live = false; };
  }, [token]);

  useEffect(() => {
    document.title = `NEOSEUM · ${order && order.cards.length === 1 ? c.one : c.head}`;
  }, [order, c]);

  let body: JSX.Element;
  if (state === 'loading') body = <p className="muted">{c.loading}</p>;
  else if (state === 'not_found') body = <div className="card error-card"><p>{c.notFound}</p></div>;
  else if (state === 'error' || !order) body = <div className="card error-card"><p>{c.error}</p></div>;
  else {
    const o = order;
    const value = o.value_kind === 'tickets' && o.tickets
      ? `${o.tickets} ${o.tickets === 1 ? 'ticket' : 'tickets'} (${money(o.card_value_cents, lang)})`
      : c.worth(money(o.card_value_cents, lang));
    const status = (s: string) => (s === 'depleted' ? c.used : s === 'blocked' ? c.blocked : s === 'expired' ? c.expired : null);
    body = (
      <>
        <section className="card">
          <p><strong>{value}</strong><br /><span className="muted small">{DESIGNS[o.design] ?? o.design}</span></p>
          {o.to || o.from ? <p className="small">{[o.to ? `${c.to} ${o.to}` : '', o.from ? `${c.from} ${o.from}` : ''].filter(Boolean).join(' · ')}</p> : null}
          {o.message ? <p className="small"><em>“{o.message}”</em></p> : null}
        </section>
        {o.cards.map((card) => {
          const st = status(card.display_status);
          return (
            <section className="card" key={card.serial}>
              <p style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '1.25rem', letterSpacing: '0.08em', margin: '0 0 0.25rem' }}>{card.code_display}</p>
              <p className="muted small" style={{ margin: 0 }}>{c.until(day(card.expires_on, lang))}{st ? ` · ${st}` : ''}</p>
              <div className="action-row">
                <a className="btn-primary" href={`${o.pdf_base}&n=${card.serial}`}>{c.pdf}</a>
                {!st ? <a className="btn-secondary" href={`https://neoseum.com/c/${card.code}`}>{c.book}</a> : null}
              </div>
            </section>
          );
        })}
        {o.cards.length > 1 ? (
          <div className="action-row"><a className="btn-primary" href={`${o.pdf_base}&n=all`}>{c.all}</a></div>
        ) : null}
        <p className="small muted">{c.how}</p>
      </>
    );
  }

  return (
    <div className="manage-shell">
      <header className="manage-head">
        <h1>NEOSEUM</h1>
        <p className="muted">{order && order.cards.length === 1 ? c.one : c.head}</p>
      </header>
      <main className="manage-main">{body}</main>
      <footer className="manage-foot muted">
        {c.questions} <a href="mailto:bookings@neoseum.com">bookings@neoseum.com</a>
      </footer>
    </div>
  );
}
